import React, { useState, useMemo } from 'react';
import { X, DollarSign, ArrowRight, Wallet, Info, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import { Shift, ExpenseItem } from '../types';

interface Props {
    onClose: () => void;
    onConfirm: (shifts: Shift) => void;
    currentBalance: number;
    totalAccumulatedGross: number;
    userId: string;
    shifts: Shift[];
    isPrivacyMode: boolean;
}

export const AdvanceCalculator: React.FC<Props> = ({ onClose, onConfirm, currentBalance, totalAccumulatedGross, userId, shifts, isPrivacyMode }) => {
    const [requestedAmount, setRequestedAmount] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);

    const totals = useMemo(() => {
        const now = new Date();
        const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

        const rhino = shifts
            .filter(s => s.date && s.date.startsWith(currentMonthStr))
            .reduce((acc, s) => acc + (s.totalEarnings || 0) + (s.appTips || 0), 0);

        const adiantas = shifts
            .filter(s => s.date && s.date.startsWith(currentMonthStr))
            .reduce((acc, s) => {
                const adv = s.expenses?.filter(e =>
                    e.description?.toLowerCase().includes('adiantamento') ||
                    e.description?.toLowerCase().includes('taxa plific')
                ).reduce((a, b) => a + b.amount, 0) || 0;
                return acc + adv;
            }, 0);

        return { rhino, adiantas, saldo: rhino - adiantas };
    }, [shifts]);

    const formatV = (v: number) => isPrivacyMode ? '****' : `R$ ${v.toFixed(2)}`;

    const { isTuesday, isTimeWindow, isNotClosingWeek } = useMemo(() => {
        const now = new Date();
        const day = now.getDay(); // 0 is Sunday, 2 is Tuesday
        const hour = now.getHours();
        const date = now.getDate();

        // New Regras de Janela
        const isClosingWeek = date <= 3 || date >= 28;
        const isPermittedTime = day === 2 && hour >= 14 && hour < 17;

        return {
            isTuesday: day === 2,
            isTimeWindow: isPermittedTime,
            isNotClosingWeek: !isClosingWeek
        };
    }, []);

    const shouldGlow = isTuesday && isTimeWindow && isNotClosingWeek;

    const amountValue = parseFloat(requestedAmount) || 0;
    const fee = amountValue * 0.04;
    const netAmount = amountValue - fee;

    const handleConfirm = () => {
        if (amountValue <= 0 || amountValue > currentBalance) return;

        const now = new Date();

        const combinedAdvanceShift: Shift = {
            id: `ADV-${Date.now()}`,
            user_id: userId,
            type: 'OFF',
            noShow: false,
            date: now.toISOString().split('T')[0],
            startTime: '00:00',
            endTime: '00:00',
            isHoliday: false,
            totalEarnings: 0,
            totalHours: 0,
            appTips: 0,
            tips: 0,
            breakdown: { basePay: 0, nightPay: 0, bilingualPay: 0 },
            expenses: [
                {
                    id: `exp-adv-${Date.now()}`,
                    category: 'FIXED',
                    origin: 'RHINO',
                    amount: netAmount,
                    description: 'Adiantamento Plific (Valor Recebido)'
                },
                {
                    id: `exp-fee-${Date.now()}`,
                    category: 'FIXED',
                    origin: 'RHINO',
                    amount: fee,
                    description: 'Taxa Plific (Custo de Adiantamento)'
                }
            ],
            extraEarnings: [],
            unpaidBreakMinutes: 0
        };

        onConfirm(combinedAdvanceShift);
        setIsConfirmed(true);
        setTimeout(() => {
            onClose();
        }, 2000);
    };

    return (
        <div 
            className="fixed inset-0 bg-black/80 z-[150] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div 
                className={`bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full max-h-[92vh] overflow-y-auto overflow-x-hidden transition-all duration-500 ${isConfirmed ? 'scale-95 opacity-0' : 'scale-100'} scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent pr-2`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6 border-b border-slate-700 pb-4">
                    <div className="flex items-center gap-3">
                        <div className="bg-orange-600/20 p-2 rounded-lg">
                            <Building2 className="w-6 h-6 text-orange-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Adiantamento Plific</h2>
                            <p className="text-xs text-slate-400">Liberação antecipada de ganhos</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* FINANCIAL SUMMARY SECTION */}
                <div className="mb-6 grid grid-cols-3 gap-2 bg-slate-900/50 p-3 rounded-xl border border-slate-700/50">
                    <div className="text-center">
                        <p className="text-[8px] uppercase font-bold text-slate-500 mb-1">Faturamento App</p>
                        <p className="text-xs font-black text-white">{formatV(totals.rhino)}</p>
                    </div>
                    <div className="text-center border-x border-slate-700/50 px-2">
                        <p className="text-[8px] uppercase font-bold text-slate-500 mb-1">Total Adiantas</p>
                        <p className="text-xs font-black text-red-400">{formatV(totals.adiantas)}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-[8px] uppercase font-bold text-slate-500 mb-1">Saldo Final</p>
                        <p className="text-xs font-black text-emerald-400">{formatV(totals.saldo)}</p>
                    </div>
                </div>

                {!isConfirmed ? (
                    <div className="space-y-6">
                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                            <div className="mb-4">
                                <div className="bg-orange-600/10 p-3.5 rounded-lg border border-orange-500/20 text-center">
                                    <span className="text-[11px] uppercase font-bold text-orange-500/70 block mb-1">Saldo do Mês (Líquido para Dedução)</span>
                                    <span className="text-xl font-black text-white">{formatV(currentBalance)}</span>
                                </div>
                            </div>

                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-xl">R$</span>
                                <input
                                    type="number"
                                    value={requestedAmount}
                                    onChange={(e) => setRequestedAmount(e.target.value)}
                                    placeholder="0,00"
                                    className="w-full bg-slate-800 border-2 border-slate-700 rounded-xl py-4 pl-12 pr-4 text-2xl font-bold text-white focus:border-orange-500 outline-none transition-all"
                                />
                                <p className="text-[10px] text-slate-500 mt-2 italic text-center">
                                    * Somente ganhos líquidos do mês atual são antecipáveis.
                                </p>
                            </div>
                        </div>

                        <div className="bg-slate-900/30 p-4 rounded-xl space-y-3 border border-slate-800">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-400 flex items-center gap-1">
                                    Taxa Plific (4%)
                                    <div className="tooltip-container">
                                        <Info className="w-3 h-3 cursor-help" />
                                        <div className="tooltip-content">Taxa fixa de processamento para adiantas via Plific.</div>
                                    </div>
                                </span>
                                <span className="text-red-400 font-medium">- R$ {fee.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-lg pt-2 border-t border-slate-800">
                                <span className="text-white font-bold">Valor Líquido</span>
                                <span className="text-green-400 font-black">R$ {netAmount.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className={`p-4 rounded-xl flex gap-3 ${shouldGlow ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-slate-900/50 border border-slate-800'}`}>
                            <AlertCircle className={`w-5 h-5 shrink-0 ${shouldGlow ? 'text-orange-500' : 'text-slate-500'}`} />
                            <p className="text-xs text-slate-400 leading-relaxed">
                                {shouldGlow
                                    ? "Você está operando dentro da janela VIP! A simulação será processada com prioridade máxima."
                                    : "Janela de operação: Terças-feiras, 14h às 17h. Simulações fora deste horário seguem o fluxo normal de 24h."}
                            </p>
                        </div>

                        <button
                            onClick={handleConfirm}
                            disabled={amountValue <= 0 || amountValue > currentBalance}
                            className={`w-full py-4 rounded-xl font-black text-lg transition-all flex items-center justify-center gap-2 shadow-xl
                ${amountValue > 0 && amountValue <= currentBalance
                                    ? (shouldGlow ? 'bg-orange-600 hover:bg-orange-500 text-white pulse-glow' : 'bg-slate-700 hover:bg-slate-600 text-white')
                                    : 'bg-slate-900 text-slate-600 cursor-not-allowed'
                                }`}
                        >
                            Confirmar Simulação <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                ) : (
                    <div className="py-12 flex flex-col items-center justify-center text-center animate-scale-in">
                        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                            <CheckCircle2 className="w-12 h-12 text-green-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Simulação Enviada!</h3>
                        <p className="text-slate-400">O valor de R$ {netAmount.toFixed(2)} será calculado em breve.</p>
                    </div>
                )}
            </div>
        </div>
    );
};
